import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { usePublicEvent } from '@/hooks/usePublicEvent';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, MapPin, Clock, Users } from 'lucide-react';
import { toast } from 'sonner';

const PublicEvent: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [eventData, setEventData] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    status: 'attending' as 'attending' | 'not_attending' | 'maybe',
    guest_count: 1,
    message: ''
  });

  const { loading, getPublicEvent, submitPublicRSVP } = usePublicEvent();

  useEffect(() => {
    const loadEvent = async () => {
      if (!slug) return;
      
      const data = await getPublicEvent(slug);
      setEventData(data);
    };

    loadEvent();
  }, [slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventData) return;

    setSubmitting(true);
    const success = await submitPublicRSVP(eventData.id, formData);
    if (success) {
      setSubmitted(true);
      toast.success('RSVP envoyé avec succès !');
    } else {
      toast.error('Erreur lors de l\'envoi du RSVP');
    }
    setSubmitting(false);
  };

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Ne pas afficher de loader, afficher le contenu quand disponible
  if (!eventData) {
    // Si pas de données et pas de chargement, afficher une erreur 
    if (!loading) {
      return (
        <div className="min-h-screen bg-page-gradient flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Événement introuvable</h1>
            <p className="text-muted-foreground">Cette page n'existe pas ou n'est plus disponible.</p>
          </div>
        </div>
      );
    }
    // Pendant le chargement, ne rien afficher (pas de loader)
    return null;
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-page-gradient flex items-center justify-center p-4">
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-green-600">
              Merci !
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              Votre réponse a été enregistrée avec succès.
            </p>
            <p className="text-sm text-muted-foreground">
              Nous avons hâte de vous voir à l'événement !
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
      {/* Hero Image Banner */}
      {eventData?.hero_image_url ? (
        <div className="relative h-80 md:h-96 w-full overflow-hidden">
          <img 
            src={eventData.hero_image_url} 
            alt={eventData?.public_title || 'Événement'}
            className="h-full w-full object-cover"
          />
          {/* Enhanced gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/50"></div>
        </div>
      ) : (
        <div className="h-20"></div>
      )}
      
      <div className="relative max-w-3xl mx-auto px-4">
        {/* Main Event Card - Overlapping Design */}
        <div className={`${eventData?.hero_image_url ? '-mt-24 md:-mt-32' : 'mt-8'} relative z-10`}>
          <Card className="bg-background shadow-2xl border-0 overflow-hidden">
            <div className="p-8 md:p-10">
              {/* Event Title */}
              <div className="text-center mb-8">
                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                  {eventData?.public_title || 'Événement'}
                </h1>
                <div className="h-1 w-20 bg-gradient-to-r from-muted-foreground to-muted-foreground/50 mx-auto rounded-full"></div>
              </div>

              {/* Event Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {eventData?.public_start_at && (
                  <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-xl">
                    <div className="p-2 bg-muted/70 rounded-lg">
                      <Calendar className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Date & Heure</h3>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(eventData.public_start_at), 'dd MMMM yyyy', { locale: fr })}
                        {(() => {
                          const startDate = new Date(eventData.public_start_at);
                          const endDate = eventData.public_end_at ? new Date(eventData.public_end_at) : null;
                          const isAllDay = startDate.getHours() === 0 && startDate.getMinutes() === 0 && 
                                          endDate && endDate.getHours() === 23 && endDate.getMinutes() === 59;
                          
                          if (!isAllDay) {
                            return (
                              <>
                                <br />
                                {format(startDate, 'HH:mm')}
                                {endDate && (
                                  <> - {format(endDate, 'HH:mm')}</>
                                )}
                              </>
                            );
                          }
                          return null;
                        })()}
                      </p>
                    </div>
                  </div>
                )}
                
                {eventData?.public_location && (
                  <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-xl">
                    <div className="p-2 bg-muted/70 rounded-lg">
                      <MapPin className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Lieu</h3>
                      <p className="text-sm text-muted-foreground">{eventData.public_location}</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Event Description */}
              {eventData?.public_description && (
                <div className="mb-8 p-6 bg-gradient-to-r from-muted/30 to-muted/10 rounded-xl">
                  <h3 className="font-semibold mb-3 flex items-center gap-3">
                    <div className="p-2 bg-muted/70 rounded-lg">
                      <Users className="h-4 w-4 text-muted-foreground" />
                    </div>
                    À propos
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{eventData.public_description}</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* RSVP Form Card */}
        {eventData?.rsvp_enabled !== false && (
          <Card className="mt-8 mb-12 shadow-xl">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
                Confirmer votre présence
              </CardTitle>
              <p className="text-muted-foreground mt-2">
                Merci de nous faire savoir si vous pourrez participer
              </p>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className="text-sm font-medium">Prénom *</Label>
                    <Input
                      id="first_name"
                      required
                      value={formData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name" className="text-sm font-medium">Nom *</Label>
                    <Input
                      id="last_name"
                      required
                      value={formData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium">Téléphone</Label>
                    <Input
                      id="phone"
                      type="tel" 
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <Label className="text-sm font-medium">Votre réponse *</Label>
                  <RadioGroup
                    value={formData.status}
                    onValueChange={(value) => handleInputChange('status', value)}
                    className="grid grid-cols-1 gap-3"
                  >
                    <div className="flex items-center space-x-3 p-4 border rounded-xl hover:bg-muted/50 transition-colors">
                      <RadioGroupItem value="attending" id="attending" />
                      <Label htmlFor="attending" className="font-medium cursor-pointer flex-1">
                        Je participe ✅
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-4 border rounded-xl hover:bg-muted/50 transition-colors">
                      <RadioGroupItem value="maybe" id="maybe" />
                      <Label htmlFor="maybe" className="font-medium cursor-pointer flex-1">
                        Peut-être 🤔
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-4 border rounded-xl hover:bg-muted/50 transition-colors">
                      <RadioGroupItem value="not_attending" id="not_attending" />
                      <Label htmlFor="not_attending" className="font-medium cursor-pointer flex-1">
                        Je ne peux pas venir ❌
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="guest_count" className="text-sm font-medium">
                    Nombre de personnes (vous inclus)
                  </Label>
                  <Input
                    id="guest_count"
                    type="number"
                    min="1"
                    max="10"
                    value={formData.guest_count}
                    onChange={(e) => handleInputChange('guest_count', parseInt(e.target.value) || 1)}
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm font-medium">
                    Message (optionnel)
                  </Label>
                  <Textarea
                    id="message"
                    placeholder="Un petit mot pour les organisateurs..."
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    className="min-h-[100px] resize-none"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 text-base font-medium" 
                  disabled={submitting}
                >
                  {submitting ? 'Envoi en cours...' : 'Confirmer ma réponse'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PublicEvent;